/**
 * Global state for the dashboard. Svelte 5 runes wrapped in a module-scoped
 * singleton so any component can import + mutate the same instance.
 *
 * State is intentionally flat — no nested objects — so $effect blocks in
 * components subscribe granularly and re-render only on the keys they read.
 */

import type { Period } from './types.js';

export type Tab = 'map' | 'rankings' | 'diagnostic';
export type TagSignal = 'Trade Promotion' | 'Distribution Push' | 'Key Account';

export interface TaggedRow {
  city_id:  number;
  city:     string;
  province: string;
  brand:    string;
  sku_id:   number;
  sku:      string;
  signal:   TagSignal;
  gpiv:     number;
  sop:      number;
  cat_gmv:  number;
  my_gmv:   number;
  share:    number;
  mp:       number;
  captured_at: string;  // period key
}

class Store {
  /* ── Period & data freshness ─────────────────────────────────────── */
  periods   = $state<Period[]>([]);
  period    = $state<string>('');
  latestKey = $derived(this.periods[0]?.key ?? '');

  /* ── Filter chain ────────────────────────────────────────────────── */
  principal   = $state<string>('');
  productType = $state<string>('');
  brand       = $state<string>('');           // optional sub-filter
  sku_id      = $state<number | null>(null);  // optional sub-filter
  citySearch  = $state<string>('');

  /* ── UI state ────────────────────────────────────────────────────── */
  tab              = $state<Tab>('map');
  selectedProvince = $state<string | null>(null);  // drill target (primary)
  selectedCity     = $state<number | null>(null);  // optional sub-drill within a province
  tagFilter        = $state<TagSignal | 'All' | null>(null);
  glossaryOpen     = $state<boolean>(false);
  taggedOpen       = $state<boolean>(false);

  /* ── Tagged-plan ─────────────────────────────────────────────────── */
  tagged = $state<Map<string, TaggedRow>>(new Map());

  /* ── Async status ────────────────────────────────────────────────── */
  bootError    = $state<string | null>(null);
  filterError  = $state<string | null>(null);   // "no data for this combination"

  /* ── Derived helpers ─────────────────────────────────────────────── */
  hasFiltersBeyondPrincipal = $derived(
    !!this.productType || !!this.brand || this.sku_id !== null
  );

  isHistorical = $derived(
    !!this.period && this.period !== this.latestKey
  );

  /* ── Mutators ────────────────────────────────────────────────────── */
  setPeriod(p: string) {
    this.period = p;
    // Per locked decision: preserve filters; warn-if-invalid is handled by
    // per-tab queries returning empty.
  }
  setPrincipal(p: string) {
    this.principal = p;
    // Cascading reset on principal change.
    this.productType = '';
    this.brand = '';
    this.sku_id = null;
  }
  setProductType(pt: string) {
    this.productType = pt;
    this.brand = '';
    this.sku_id = null;
  }
  setBrand(b: string) {
    this.brand = b;
    this.sku_id = null;
  }
  setSku(s: number | null) { this.sku_id = s; }
  setTab(t: Tab) { this.tab = t; }
  selectProvince(name: string | null) {
    this.selectedProvince = name;
    this.selectedCity = null;        // resetting sub-drill when province changes
    if (name !== null) this.tab = 'diagnostic';
  }
  selectCity(id: number | null) {
    this.selectedCity = id;
    if (id !== null) this.tab = 'diagnostic';
  }
  clearDrill() {
    this.selectedProvince = null;
    this.selectedCity = null;
  }

  /* ── Tagging ─────────────────────────────────────────────────────── */
  tagKey(city_id: number, sku_id: number): string {
    return `${city_id}|${sku_id}`;
  }
  isTagged(city_id: number, sku_id: number): boolean {
    return this.tagged.has(this.tagKey(city_id, sku_id));
  }
  setTag(row: TaggedRow) {
    this.tagged.set(this.tagKey(row.city_id, row.sku_id), row);
    this.tagged = new Map(this.tagged);  // force reactivity on Map mutations
  }
  untag(city_id: number, sku_id: number) {
    this.tagged.delete(this.tagKey(city_id, sku_id));
    this.tagged = new Map(this.tagged);
  }
  clearTags() {
    this.tagged = new Map();
  }
}

export const store = new Store();
